import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";

import { UpdateUser } from "../utils/usersAPI";
import { setLoggedUser } from "../features/userSlice";
import { UpdateUserAddress } from "../utils/userAddressAPI";

const emptyErrors = {
  name: "",
  email: "",
  phone: "",
  city: "",
  street: "",
  houseNumber: "",
};

const UserDetailsSection = () => {
  const dispatch = useDispatch();

  const loggedUser = useSelector(
    (state) => state.user.logedUser
  );

  const firstFieldRef = useRef(null);
  const editButtonRef = useRef(null);

  const [isEditing, setIsEditing] =
    useState(false);

  const [isSaving, setIsSaving] =
    useState(false);

  const [statusMessage, setStatusMessage] =
    useState("");

  const [errors, setErrors] =
    useState(emptyErrors);

  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    street: "",
    houseNumber: "",
    apartment: "",
    floor: "",
    entrance: "",
    notes: "",
  });

  const getFormFromLoggedUser = () => {
    const address =
      loggedUser?.userAddresses?.[0];

    return {
      name: loggedUser?.name || "",
      email: loggedUser?.email || "",
      phone: loggedUser?.phone || "",
      city: address?.city || "",
      street: address?.street || "",
      houseNumber:
        address?.houseNumber || "",
      apartment: address?.apartment || "",
      floor: address?.floor || "",
      entrance: address?.entrance || "",
      notes: address?.notes || "",
    };
  };

  useEffect(() => {
    if (loggedUser) {
      setUserForm(getFormFromLoggedUser());
    }
  }, [loggedUser]);

  useEffect(() => {
    if (isEditing) {
      window.setTimeout(() => {
        firstFieldRef.current?.focus();
      }, 100);
    }
  }, [isEditing]);

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      phone: "",
      city: "",
      street: "",
      houseNumber: "",
    };

    if (!userForm.name.trim()) {
      newErrors.name = "יש להזין שם";
    }

    if (!userForm.email.trim()) {
      newErrors.email =
        "יש להזין כתובת אימייל";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        userForm.email.trim()
      )
    ) {
      newErrors.email =
        "כתובת האימייל אינה תקינה";
    }

    if (!userForm.phone.trim()) {
      newErrors.phone =
        "יש להזין מספר טלפון";
    } else if (
      !/^[0-9+\-\s()]{7,15}$/.test(
        userForm.phone.trim()
      )
    ) {
      newErrors.phone =
        "מספר הטלפון אינו תקין";
    }

    if (!userForm.city.trim()) {
      newErrors.city = "יש להזין עיר";
    }

    if (!userForm.street.trim()) {
      newErrors.street = "יש להזין רחוב";
    }

    if (
      !String(userForm.houseNumber).trim()
    ) {
      newErrors.houseNumber =
        "יש להזין מספר בניין";
    }

    setErrors(newErrors);

    return !Object.values(newErrors).some(
      Boolean
    );
  };

  const handleUserFieldChange = (event) => {
    const { name, value } = event.target;

    setUserForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }

    if (statusMessage) {
      setStatusMessage("");
    }
  };

  const handleStartEdit = () => {
    setErrors(emptyErrors);
    setStatusMessage("");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setUserForm(getFormFromLoggedUser());
    setErrors(emptyErrors);
    setStatusMessage("");
    setIsEditing(false);

    window.setTimeout(() => {
      editButtonRef.current?.focus();
    }, 100);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    setStatusMessage("");

    if (!validateForm()) {
      window.setTimeout(() => {
        const firstErrorField =
          document.querySelector(
            '[aria-invalid="true"]'
          );

        firstErrorField?.focus();
      }, 100);

      return;
    }

    try {
      setIsSaving(true);

      const currentAddress =
        loggedUser.userAddresses?.[0];

      if (!currentAddress) {
        await Swal.fire({
          title: "לא נמצאה כתובת לעדכון",
          icon: "warning",
          confirmButtonText: "אישור",
          confirmButtonColor: "#D81B60",
        });

        return;
      }

      const updatedUser = {
        id: loggedUser.id,
        name: userForm.name.trim(),
        email: userForm.email.trim(),
        phone: userForm.phone.trim(),
        address:
          loggedUser.address || "",
        tipeUser: loggedUser.tipeUser,
        hasPermanentDiscount:
          loggedUser.hasPermanentDiscount,
      };

      const updatedAddress = {
        ...currentAddress,
        userId: loggedUser.id,
        city: userForm.city.trim(),
        street: userForm.street.trim(),
        houseNumber: String(
          userForm.houseNumber
        ).trim(),
        apartment:
          userForm.apartment.trim(),
        floor: userForm.floor.trim(),
        entrance:
          userForm.entrance.trim(),
        notes: userForm.notes.trim(),
        isDefault:
          currentAddress?.isDefault ??
          true,
      };

      await UpdateUser(
        loggedUser.id,
        updatedUser
      );

      const addressResponse =
        await UpdateUserAddress(
          currentAddress.id,
          updatedAddress
        );

      dispatch(
        setLoggedUser({
          ...loggedUser,
          ...updatedUser,
          userAddresses: [
            addressResponse.data,
          ],
        })
      );

      setStatusMessage(
        "פרטי הלקוח עודכנו בהצלחה"
      );

      await Swal.fire({
        title: "הפרטים עודכנו בהצלחה",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      setIsEditing(false);

      window.setTimeout(() => {
        editButtonRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error(
        "שגיאה בעדכון פרטי המשתמש:",
        error
      );

      setStatusMessage(
        "אירעה שגיאה בעדכון פרטי הלקוח"
      );

      await Swal.fire({
        title:
          "שגיאה בעדכון פרטי המשתמש",
        text:
          "לא הצלחנו לשמור את הפרטים. נסי שוב בעוד כמה רגעים.",
        icon: "error",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",

      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
        {
          borderColor: "#D81B60",
          borderWidth: "2px",
        },

      "&.Mui-error .MuiOutlinedInput-notchedOutline":
        {
          borderColor: "#d32f2f",
        },

      "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline":
        {
          borderColor: "#d32f2f",
          borderWidth: "2px",
        },
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#D81B60",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#d32f2f",
    },

    "& input:focus, & textarea:focus": {
      outline: "none",
    },
  };

  const focusStyle = {
    "&:focus-visible": {
      outline:
        "3px solid #D81B60",
      outlineOffset: "3px",
    },
  };

  if (!loggedUser) {
    return null;
  }

  const address =
    loggedUser.userAddresses?.[0];

  return (
   <Box
  component="section"
  aria-labelledby="user-details-heading"
  sx={{
    mb: 3,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    
  }}
>
      <Box
       sx={{
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1,
  mb: 1.5,
}}
      >
        <Typography
          id="user-details-heading"
          component="h3"
          variant="h6"
        >
          פרטי לקוח
        </Typography>

        {!isEditing ? (
          <IconButton
            ref={editButtonRef}
            type="button"
            size="small"
            onClick={handleStartEdit}
            aria-label="עריכת פרטי הלקוח"
            title="עריכת פרטי הלקוח"
            sx={focusStyle}
          >
            <EditIcon
              fontSize="small"
              aria-hidden="true"
            />
          </IconButton>
        ) : (
          <IconButton
            type="button"
            size="small"
            onClick={handleCancelEdit}
            disabled={isSaving}
            aria-label="סגירת עריכת פרטי הלקוח ללא שמירה"
            title="ביטול עריכה"
            sx={focusStyle}
          >
            <CloseIcon
              fontSize="small"
              aria-hidden="true"
            />
          </IconButton>
        )}
      </Box>

      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        {statusMessage}
      </Box>

      {!isEditing && (
        <Box
          component="dl"
          sx={{
  m: 0,
  display: "grid",
  gap: 0.8,
  width: "100%",
  minWidth: 0,

  "& > div": {
    minWidth: 0,
    maxWidth: "100%",
  },

  "& dt": {
    fontWeight: 700,
    display: "inline",
  },

  "& dd": {
    display: "inline",
    m: 0,
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
}}
        >
          <Box component="div">
            <Typography
              component="dt"
              variant="body1"
            >
              שם:{" "}
            </Typography>

            <Typography
              component="dd"
              variant="body1"
            >
              {loggedUser.name ||
                "לא הוזן"}
            </Typography>
          </Box>

          <Box component="div">
            <Typography
              component="dt"
              variant="body1"
            >
              אימייל:{" "}
            </Typography>

            <Typography
  component="dd"
  variant="body1"
  dir="ltr"
  sx={{
    display: "inline",
    overflowWrap: "anywhere",
    wordBreak: "break-all",
  }}
>
  {loggedUser.email || "לא הוזן"}
</Typography>
          </Box>

          <Box component="div">
            <Typography
              component="dt"
              variant="body1"
            >
              טלפון:{" "}
            </Typography>

            <Typography
              component="dd"
              variant="body1"
              dir="ltr"
              sx={{
                display: "inline-block",
              }}
            >
              {loggedUser.phone ||
                "לא הוזן"}
            </Typography>
          </Box>

          <Box component="div">
            <Typography
              component="dt"
              variant="body1"
            >
              כתובת למשלוח:{" "}
            </Typography>

            <Typography
              component="dd"
              variant="body1"
            >
              {address
                ? [
                    address.city,
                    `${address.street || ""} ${
                      address.houseNumber ||
                      ""
                    }`.trim(),
                    address.apartment
                      ? `דירה ${address.apartment}`
                      : "",
                    address.floor
                      ? `קומה ${address.floor}`
                      : "",
                    address.entrance
                      ? `כניסה ${address.entrance}`
                      : "",
                  ]
                    .filter(Boolean)
                    .join(", ")
                : "לא הוגדרה כתובת"}
            </Typography>
          </Box>
        </Box>
      )}

      {isEditing && (
        <Box
  component="form"
  noValidate
  onSubmit={handleSave}
  aria-label="טופס עריכת פרטי הלקוח"
  sx={{
    display: "grid",
    gap: 1.5,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,

    "& .MuiFormControl-root": {
      minWidth: 0,
      maxWidth: "100%",
    },
  }}
>
          <TextField
            inputRef={firstFieldRef}
            id="customer-name"
            label="שם"
            name="name"
            required
            value={userForm.name}
            onChange={
              handleUserFieldChange
            }
            autoComplete="name"
            fullWidth
            error={Boolean(errors.name)}
            helperText={
              errors.name || " "
            }
            inputProps={{
              "aria-invalid":
                Boolean(errors.name),
            }}
            sx={fieldStyle}
          />

          <TextField
            id="customer-email"
            label="אימייל"
            name="email"
            required
            type="email"
            value={userForm.email}
            onChange={
              handleUserFieldChange
            }
            autoComplete="email"
            fullWidth
            error={Boolean(errors.email)}
            helperText={
              errors.email || " "
            }
            inputProps={{
              "aria-invalid":
                Boolean(errors.email),
            }}
            sx={fieldStyle}
          />

          <TextField
            id="customer-phone"
            label="טלפון"
            name="phone"
            required
            type="tel"
            value={userForm.phone}
            onChange={
              handleUserFieldChange
            }
            autoComplete="tel"
            fullWidth
            error={Boolean(errors.phone)}
            helperText={
              errors.phone || " "
            }
            inputProps={{
              inputMode: "tel",
              dir: "ltr",
              "aria-invalid":
                Boolean(errors.phone),
            }}
            sx={fieldStyle}
          />

          <TextField
            id="customer-city"
            label="עיר"
            name="city"
            required
            value={userForm.city}
            onChange={
              handleUserFieldChange
            }
            autoComplete="address-level2"
            fullWidth
            error={Boolean(errors.city)}
            helperText={
              errors.city || " "
            }
            inputProps={{
              "aria-invalid":
                Boolean(errors.city),
            }}
            sx={fieldStyle}
          />

          <TextField
            id="customer-street"
            label="רחוב"
            name="street"
            required
            value={userForm.street}
            onChange={
              handleUserFieldChange
            }
            autoComplete="address-line1"
            fullWidth
            error={
              Boolean(errors.street)
            }
            helperText={
              errors.street || " "
            }
            inputProps={{
              "aria-invalid":
                Boolean(errors.street),
            }}
            sx={fieldStyle}
          />

          <TextField
            id="customer-house-number"
            label="מספר בניין"
            name="houseNumber"
            required
            value={
              userForm.houseNumber
            }
            onChange={
              handleUserFieldChange
            }
            autoComplete="address-line2"
            fullWidth
            error={Boolean(
              errors.houseNumber
            )}
            helperText={
              errors.houseNumber || " "
            }
            inputProps={{
              inputMode: "numeric",
              "aria-invalid": Boolean(
                errors.houseNumber
              ),
            }}
            sx={fieldStyle}
          />

          <TextField
            id="customer-apartment"
            label="דירה (אופציונלי)"
            name="apartment"
            value={userForm.apartment}
            onChange={
              handleUserFieldChange
            }
            inputProps={{
              inputMode: "numeric",
            }}
            fullWidth
            sx={fieldStyle}
          />

          <TextField
            id="customer-floor"
            label="קומה (אופציונלי)"
            name="floor"
            value={userForm.floor}
            onChange={
              handleUserFieldChange
            }
            inputProps={{
              inputMode: "numeric",
            }}
            fullWidth
            sx={fieldStyle}
          />

          <TextField
            id="customer-entrance"
            label="כניסה (אופציונלי)"
            name="entrance"
            value={userForm.entrance}
            onChange={
              handleUserFieldChange
            }
            fullWidth
            sx={fieldStyle}
          />

          <TextField
            id="customer-notes"
            label="הערות למשלוח (אופציונלי)"
            name="notes"
            value={userForm.notes}
            onChange={
              handleUserFieldChange
            }
            fullWidth
            multiline
            rows={3}
            sx={fieldStyle}
          />

          <Box
           sx={{
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 1,
  mt: 1,

  "& .MuiButton-root": {
    width: {
      xs: "100%",
      sm: "auto",
    },
  },
}}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancelEdit}
              disabled={isSaving}
              sx={{
                minHeight: 44,
                borderColor: "#D81B60",
                color: "#AD1457",

                "&:hover": {
                  borderColor:
                    "#AD1457",
                  backgroundColor:
                    "rgba(216, 27, 96, 0.04)",
                },

                ...focusStyle,
              }}
            >
              ביטול
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              aria-busy={isSaving}
              sx={{
                minHeight: 44,
                minWidth: 130,
                backgroundColor:
                  "#D81B60",

                "&:hover": {
                  backgroundColor:
                    "#AD1457",
                },

                "&.Mui-disabled": {
                  backgroundColor:
                    "#9e9e9e",
                  color: "#ffffff",
                },

                ...focusStyle,
              }}
            >
              {isSaving ? (
                <>
                  <CircularProgress
                    size={20}
                    aria-hidden="true"
                    sx={{
                      color: "inherit",
                      ml: 1,
                    }}
                  />

                  <span>שומרת...</span>
                </>
              ) : (
                "שמור פרטים"
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserDetailsSection;